/**
 * ── SECTION LAYOUT: FEATURED CARDS ⇄ LISTING TABLE (2026-08-21, MVP) ──────────
 * Jack: "do want the option to toggle switch this to a listing table just for
 * the mvp" — then, on the mockup: "why are you mocking it up when you can just
 * pull directly from sfmc block." He was right twice over: "07 - Listing Table"
 * (SFMC 1027211) is a REAL block in Content Builder with AUTHORED guardrails
 * (module `listingTable`; rows are homesite data — never typed; only the group
 * heading and the View link are editable), it just isn't in any ECH template
 * order yet. It is admitted to the registry by id (`ECH_EXTRA_IDS`), so the
 * toggle swaps between two real SFMC blocks — nothing is mocked, nothing is
 * invented.
 *
 * The move is a SECTION swap, not a field map:
 * - cards → table: the WHOLE card set (every Featured block of this type) is
 *   stashed verbatim on the table block (`__layStash`), the sibling blocks are
 *   removed, and the selected block becomes the table via the same in-place
 *   swap the 1CTA/2CTA strip uses. The table ships Layne's own markup.
 * - table → cards: the stash comes back byte-for-byte; a table placed with no
 *   stash becomes one default Featured (2-button) block. Toggling is lossless
 *   for the session either way.
 */
import { echMeta, echByName } from "./echRegistry";
import { echSwapTo, type SwapResult } from "./echSwap";
import type { DynBlock } from "./echDyn";
import type { Pack } from "./dynamicFill";

/**
 * ── THE TOGGLE TARGETS THE GOVERNED TABLE, NOT THE 07 BLOCK (2026-08-22) ────────
 * Jack, on Available Now with Edenbrooke picked: "why aren't you auto populating the
 * homes for this community?" Because the toggle was swapping in **07 - Listing Table**
 * (SFMC 1027211, module `listingTable`), whose authored guardrails declare TWO editable
 * fields — the group heading and the View link — and whose ten rows are literal
 * placeholder markup ("101 / Plan Name / $399,990"). There is no `home1Homesite` on it,
 * so no `ECH_DYN` map can reach it and nothing can ever fill it. Measured in the live
 * block feed: 1027211 → 2 fields, **ECH_ListingTable (1028584, module `communityTable`)
 * → 73 fields** (3 + 10 homes × 7), and the latter is exactly what
 * `ECH_TPL_AvailableNow` names as the sanctioned alternate for ECH_Featured2CTA.
 *
 * So the toggle now swaps to the governed block and falls back to 07 only if the
 * governed one is missing from the registry. 07 stays admitted by id (`ECH_EXTRA_IDS`)
 * because a template may still place it, and Layne owns whether it lives on.
 */
const LISTING_TABLE_FED = "ECH_ListingTable";      /* 1028584 · communityTable · 73 fields */
const LISTING_TABLE_FLAT = "07 - Listing Table";   /* 1027211 · listingTable  · 2 fields  */
export const listingTableName = (): string =>
  (echByName(LISTING_TABLE_FED) ? LISTING_TABLE_FED : LISTING_TABLE_FLAT);
/** @deprecated read `listingTableName()` — kept so nothing imports a stale constant */
export const LISTING_TABLE_NAME = LISTING_TABLE_FLAT;
export const LAY_STASH = "__layStash";
/* ── ONE BUTTON BY DEFAULT (2026-09-10, Layne per Justin: "Default featured cards
   should be the 1 CTA module; option to add 2nd CTA") ─────────────────────────────
   The toggle used to mint ECH_Featured2CTA. The Inspector's swap strip (echSwap.ts,
   `featured` group) is the "option to add a 2nd" — one click, copy carried. 2CTA stays
   the fallback for a library where the 1CTA block has not been pulled yet. */
const CARDS_ONE = "ECH_Featured1CTA", CARDS_TWO = "ECH_Featured2CTA";
const cardsDefault = (): string => (echByName(CARDS_ONE) ? CARDS_ONE : CARDS_TWO);

export interface LayStashEntry { type: string; data: Record<string, unknown> }

/** is this block A table rendering of the section? Covers BOTH tables (2026-08-21,
    Jack on the Community Home Table's panel: "why can't we choose between listing
    table and featured home"): the id-admitted 07 block (module listingTable) and
    Layne's governed Community Home Table (module communityTable, the one the
    templates actually place). Module, not name — 07's name carries no ECH_ prefix,
    which is exactly why it slipped the registry. */
export function isListingTable(type: string): boolean {
  const m = echMeta(type)?.gr.module;
  return m === "listingTable" || m === "communityTable";
}

/** the toggle only renders when its target block is actually registered */
export function layTableReady(): boolean { return !!echByName(listingTableName()); }
export function layCardsReady(): boolean { return !!echByName(cardsDefault()); }

/**
 * ── THE SWAP CARRIES THE DYNAMIC STATE (2026-08-22) ───────────────────────────
 * Jack, switching a filled Community Home Table to Plan cards: "tried switching
 * from table to featured and shows this … need to make this more fluid." What he
 * saw was a card with bare labels ("sq ft / beds / baths", no values) and a panel
 * reading "Choose a plan · 0 selected" — because a FRESH swap (no stash from a
 * previous toggle) built the target block from its registry DEFAULT and carried
 * nothing across. The rows were right there on the block he was switching away
 * from.
 *
 * These four control keys are what "what this block is showing" actually means:
 * the mode, the feature level, and the two stored picks. None of them is a
 * declared SFMC field — they are all `__`-prefixed state this repo owns — so
 * copying them onto the target is safe for any pair of blocks, and the
 * document-wide fill repaints the target from them on the same tick.
 *
 * THE ONE ASYMMETRY: a Featured block renders ONE card while the table has ten
 * rows, so a home pick travelling table → card is trimmed to its first entry.
 * Sending all ten would store a list the card cannot show, which is the
 * two-renderings-disagree fault — and going back the other way the full list is
 * restored from the stash anyway.
 */
const DYN_CARRY = ["__echMode", "__featLevel", "__planPick"] as const;
const HOME_PICK = "__homePick";

function carryDyn(from: DynBlock, into: Record<string, unknown>, toCards: boolean): void {
  const src = (from && from.data) || {};
  DYN_CARRY.forEach((k) => { const v = src[k]; if (v !== undefined && v !== "") into[k] = v; });
  /* ── THE PICK LIST TRAVELS WHOLE (2026-08-28) ─────────────────────────────────
     Was trimmed to `hp.split("\n")[0]` on a cards-bound swap — the "ONE ASYMMETRY"
     above — on the reasoning that a Featured block renders one card. It renders up
     to five, and the mint in `doLayout` now seeds three cards from a single
     community or one per community across several. Trimming here threw away the very
     rows that mint reads, which is why a single-community table produced one card.
     The mint bounds the count; this no longer pre-empts it. */
  const hp = String(src[HOME_PICK] == null ? "" : src[HOME_PICK]).trim();
  if (hp) into[HOME_PICK] = hp;
}

/** an entry snapshot with the stash key stripped, so stashes never nest */
function snap(type: string, data: Record<string, unknown>): LayStashEntry {
  const d = { ...data };
  delete d[LAY_STASH];
  return { type, data: d };
}

function readStash(self: DynBlock): LayStashEntry[] {
  const raw = self && self.data ? self.data[LAY_STASH] : null;
  return Array.isArray(raw) ? (raw as LayStashEntry[]).filter((e) => e && e.type && e.data) : [];
}

/** cards → table: the table this section was swapped FROM comes back with its own
    rows when the card carries a stash; a fresh section gets the governed table (see listingTableName). Either
    way the WHOLE card set rides along as the new stash. */
export function layToTable(self: DynBlock, fromName: string, cards: LayStashEntry[], packs: Pack[] = []): SwapResult | null {
  const cardSnaps = cards.map((c) => snap(c.type, c.data));
  /* ── THE STASH BELONGS TO THE SECTION, NOT TO WHICHEVER CARD IS SELECTED (2026-09-18) ──
     table → cards parks the typed table on the FIRST card; the requester then adds or
     selects another card and toggles back — and `self` is that other card, which carries
     no stash. Before this the fresh-table path ran and every typed row was gone (Laura
     Hughes, pilot call: "it would delete the info that she had already entered"). Look
     on the selected card first, then across the whole card set. */
  let st = readStash(self);
  if (!(st.length && isListingTable(st[0].type))) {
    for (const c of cards) {
      const cs = readStash({ data: c.data } as DynBlock);
      if (cs.length && isListingTable(cs[0].type)) { st = cs; break; }
    }
  }
  if (st.length && isListingTable(st[0].type)) {
    return { type: st[0].type, data: { ...st[0].data, [LAY_STASH]: cardSnaps } };
  }
  const r = echSwapTo(self, fromName, listingTableName());
  if (!r) return null;
  carryDyn(self, r.data, false);
  /* ── EVERY CARD BECOMES A ROW, NOT JUST THE SELECTED ONE (2026-09-15) ────────────
     Driven on dev: four Featured cards (Charle II, Edison, Georgia, Conway) → Listing
     table → ONE row, Conway's — the card the panel happened to be open on. `carryDyn`
     carries `self`'s own pick, and the siblings went into the stash, which keeps the
     round-trip lossless but leaves the TABLE showing a quarter of the section. The
     table's `__planPick` is a newline list (see planPickList), so it takes one line
     per card, in document order: the card's own pick when it has one, else a pick
     MINTED from the card's plan name against the packs in hand (an auto-filled card
     never stored one). A card nothing matches is skipped rather than guessed at. A
     section holding plan rows is read at plans level, so the level rides along. */
  const lines: string[] = [];
  const seen = new Set<string>();
  const push = (k: string) => { const t = String(k || "").trim(); if (t && !seen.has(t)) { seen.add(t); lines.push(t); } };
  cards.forEach((c) => {
    const d = c.data || {};
    const own = String(d.__planPick == null ? "" : d.__planPick).trim();
    if (own) { own.split("\n").forEach(push); return; }
    const nm = String(d.PlanName == null ? "" : d.PlanName).trim().toLowerCase();
    if (!nm) return;
    for (const pk of packs) {
      const hit = (pk.C.plans || []).find((pl) => String(pl.name || "").trim().toLowerCase() === nm);
      if (hit) {
        const num = String(hit.num == null ? "" : hit.num).trim();
        if (num || hit.name) { push(pk.code + "|pn|" + num + "|" + String(hit.name || "").trim()); return; }
      }
    }
  });
  if (lines.length) {
    r.data.__planPick = lines.join("\n");
    r.data.__featLevel = "plans";
  }
  r.data[LAY_STASH] = cardSnaps;
  return r;
}

/** table → cards: the stashed card set verbatim, or one default Featured block
    when fresh — and the TABLE itself is stashed on the first card, rows intact,
    so toggling back restores it byte-for-byte.

    ── `fresh` TELLS THE CALLER WHICH OF THOSE TWO HAPPENED (2026-08-27) ────────────
    The caller has to seed cards from the table's rows on a FRESH swap and must NOT
    on a stash restore, where `first.data` already holds the requester's own card
    selection. It was inferring that from "does the data carry a pick", which gets the
    answer exactly backwards on the case that matters: `carryDyn` brings the table's
    `__homePick` across, so a fresh swap LOOKS like it already has a pick and the
    seeding — including the `__featLevel` write it does alongside — was skipped.
    That is the "switch to Plan cards and nothing shows" defect. The distinction is
    structural, so report it rather than let the caller guess. */
export function layToCards(
  self: DynBlock,
  fromName: string,
): { first: SwapResult; rest: LayStashEntry[]; fresh: boolean } | null {
  const tableSnap = snap(self.type, self.data || {});
  const st = readStash(self);
  if (st.length) {
    return {
      first: { type: st[0].type, data: { ...st[0].data, [LAY_STASH]: [tableSnap] } },
      rest: st.slice(1),
      fresh: false,
    };
  }
  const r = echSwapTo(self, fromName, cardsDefault());
  if (!r) return null;
  carryDyn(self, r.data, true);
  r.data[LAY_STASH] = [tableSnap];
  return { first: r, rest: [], fresh: true };
}
